import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Lock, Mail, Eye, EyeOff } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        fontFamily: 'Manrope, system-ui, sans-serif',
      }}>
      <form onSubmit={handleSubmit} className="w-full max-w-sm mx-4"
        style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 16,
          padding: '48px 36px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        }}>
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            <Lock className="h-7 w-7 text-white" />
          </div>
        </div>
        <h1 className="text-center text-2xl font-bold text-slate-100 mb-1">HireFast AI</h1>
        <p className="text-center text-sm text-slate-400 mb-8">Sign in to your account</p>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-slate-500" /> Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoFocus
              data-testid="login-email-input"
              className="bg-[#0f172a] border-slate-600 text-slate-100 placeholder:text-slate-600 h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-slate-500" /> Password
            </Label>
            <div className="relative">
              <Input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                data-testid="login-password-input"
                className="bg-[#0f172a] border-slate-600 text-slate-100 placeholder:text-slate-600 h-11 pr-10"
              />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <p className="text-red-400 text-sm mt-4 text-center" data-testid="login-error">{error}</p>
        )}

        <Button type="submit" disabled={loading}
          className="w-full mt-6 h-11 font-semibold text-white"
          style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
          data-testid="login-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>

        <p className="text-center text-[11px] text-slate-600 mt-6">
          Powered by TekWissen
        </p>
        <div className="text-center mt-3">
          <a href="/developers" className="text-[11px] text-indigo-500 hover:text-indigo-700 font-medium transition-colors" data-testid="developer-docs-link">
            API Documentation for Partners
          </a>
        </div>
      </form>
    </div>
  );
}
